/**
 * The templates domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted
 * in flags fan-out batch 2).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/templates/flags` publishes
 * both. `/contributions` carries nothing for flags — this domain HAS such a
 * door, holding the analytics event-name declarations of D9 ruling 27, and
 * neither descriptor below belongs in it.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard (cache keys do), so a second spelling would be
 * invisible to every other gate in the repo.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/templates/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/templates/flags`, so its importers and
 * their `jest.mock` literals are untouched.
 *
 * ONE MORE GETTER ARRIVED WITH THE WHOLE-CLASS FAN-OUT. `useIsXmlFormEnabled`
 * gates XML form templates — `.xml` uploads in the form-template dropzone, the
 * XML review step, the XML session document view, and form-DSL add / delete /
 * re-type — and it is templates' by surface rather than by key prefix: the key
 * was minted under `release-growth-` and a key is a frozen external identity,
 * so it keeps that spelling here. Its production importers are the
 * shared-templates hooks, the form-filling modals and the session document
 * view.
 *
 * WHAT DOES NOT BELONG HERE — two of this domain's four app-barrel getters are
 * NOT below, and both are named rather than silently skipped.
 * `useIsTemplatePinningEnabled` reads `release-pinned-template-auto-generation`
 * through `useFeatureFlagEnabled` from `@/app-shell/flags/posthog-compat`, which
 * resolves from posthog-js on cloud and only falls back to OpenFeature on the
 * airgapped build. `@heidi/runtime/feature-flags` carries no PostHog surface, so
 * rewriting it onto the door would change WHICH PROVIDER answers
 * (`monorepo-wave-1-p3-feature-flags` Z4). Its app-barrel site is deliberately
 * left UNTOUCHED by this conversion for a second reason on top of the first: a
 * change retiring that gate outright is in flight against the same file, so
 * annotating code that is being deleted would buy a conflict and nothing else.
 * The classification is recorded here instead.
 * `useIsPinnedTemplateNotegenUiEnabled` ANDs that same constant-bodied getter,
 * so it inherits the disqualification by composition: a `() => true` body is not
 * a descriptor read, and a non-descriptor body cannot enter a file whose whole
 * claim is that everything in it reads one.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
} from '@heidi/runtime/feature-flags';
import { isTauri } from '@heidi/runtime/platform';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on the four established template flags, measured rather than
// assumed. Templates are a
// clinical face of the product on every build profile: `src/pages/scribe/my-templates`
// renders on the appliance — its `isOnPrem` branches withhold the community and
// marketplace arms, not the page or its table — and the compliance fix action
// lives in the template editor, which has no build gate at all. Omitting the
// field would take the D6 default (`'cloud-only'`) and withhold surfaces the
// appliance already ships. The XML form gate is the clearest of the three: its
// own app-barrel docblock recorded that on-prem it resolves from the baked
// bundle, pinned on.
// ---------------------------------------------------------------------------

/**
 * The template-compliance "Fix for me" action. A rendering gate only: the
 * per-flow compliance flags still decide eligibility once this rollout gate is
 * on, so OFF hides the action and ON restores the pre-existing eligibility
 * logic rather than bypassing it.
 */
export const templateComplianceFixForMeFlag = defineFeatureFlag({
  key: 'release-scribe-template-compliance-fix-for-me',
  defaultValue: false,
  description: 'The template-compliance "Fix for me" action',
  builds: 'all',
});

/**
 * DS-957: the redesigned template library data table — the design-system
 * `DataTable` list variant replacing the bespoke `TemplateTable`. Both accept
 * the same props, so the flag swaps a component identity and nothing else;
 * flipped per-environment in Flagsmith.
 */
export const templatesTableRedesignFlag = defineFeatureFlag({
  key: 'release-templates-table-redesign',
  defaultValue: false,
  description: 'The redesigned template library data table',
  builds: 'all',
});

/**
 * Desktop template-scoped SystmOne flow authoring. Rollout/kill gate only:
 * exposure is intentionally not tracked as an experiment.
 *
 * `builds` is explicitly `cloud-only`: the native RPA bridge is not an on-prem
 * product surface.
 */
export const rpaFlowFlag = defineFeatureFlag({
  key: 'release-scribe-enable-s1-rpa-flow',
  defaultValue: false,
  description: 'Desktop template-scoped SystmOne flow authoring',
  builds: 'cloud-only',
});

/**
 * Magic Flow onboarding entry points (template CTAs and the onboarding intro), on web
 * and desktop. Separate from `rpaFlowFlag` so onboarding and the desktop authoring
 * pilot roll out and roll back independently. Rollout/kill gate only.
 *
 * `builds` is `cloud-only` for the same reason as `rpaFlowFlag`: Magic Flow is
 * not an on-prem product surface.
 */
export const magicFlowOnboardingFlag = defineFeatureFlag({
  key: 'release-scribe-enable-magic-flow-onboarding',
  defaultValue: false,
  description: 'Magic Flow onboarding entry points on web and desktop',
  builds: 'cloud-only',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// ONE DISCLOSED BEHAVIOURAL DELTA, common to all three and a gain rather than a
// risk: `useBooleanFlag` honours the injected `forceEnableAll` override, which
// the `useBooleanFlagValue` calls these replace did not. That is the choke point
// the door exists for (design D8) and the knob is dev-only. NO body carried a
// hand-rolled `process.env` branch, so this domain has no forceAll collapse to
// disclose — unlike its two batch siblings.
// ---------------------------------------------------------------------------

/** The template-compliance "Fix for me" action. */
export const useIsTemplateComplianceFixForMeEnabled = (): boolean =>
  useBooleanFlag(
    templateComplianceFixForMeFlag.key,
    templateComplianceFixForMeFlag.defaultValue
  );

/** The redesigned template library data table. */
export const useIsTemplatesTableRedesignEnabled = (): boolean =>
  useBooleanFlag(templatesTableRedesignFlag.key, templatesTableRedesignFlag.defaultValue);

/** Desktop template-scoped SystmOne flow authoring. */
export const useIsRpaFlowEnabled = (): boolean => {
  const isEnabled = useBooleanFlag(rpaFlowFlag.key, rpaFlowFlag.defaultValue);
  return isTauri() && isEnabled;
};

/** Magic Flow onboarding entry points; web-safe, no desktop check. */
export const useIsMagicFlowOnboardingFlagEnabled = (): boolean =>
  useBooleanFlag(magicFlowOnboardingFlag.key, magicFlowOnboardingFlag.defaultValue);

/**
 * XML form templates: `.xml` uploads in the form-template dropzone, the XML
 * review step, the XML session document view, add / delete / re-type in the
 * form-DSL editor (MOD-1643), and the template-community XML preview. One
 * flag for the whole surface.
 */
export const xmlFormFlag = defineFeatureFlag({
  key: 'release-growth-enable-xml-form',
  defaultValue: false,
  description:
    'XML form templates: upload, review, document view, form-DSL editing, and template community preview',
  builds: 'all',
});

/** XML form templates. */
export const useIsXmlFormEnabled = (): boolean =>
  useBooleanFlag(xmlFormFlag.key, xmlFormFlag.defaultValue);

/**
 * JSON form templates: `.json` uploads, the review step, field edits, and
 * community publish. Fill and download of a template that already exists stay
 * available when this is off. Default off until the backend PRs are deployed.
 */
export const jsonFormFlag = defineFeatureFlag({
  key: 'release-growth-enable-json-form',
  defaultValue: false,
  description: 'JSON form templates: upload, review, field edits, community publish',
  builds: 'all',
});

/** JSON form templates. */
export const useIsJsonFormEnabled = (): boolean =>
  useBooleanFlag(jsonFormFlag.key, jsonFormFlag.defaultValue);

/**
 * Pinned on by the on-prem bundle: the appliance's gateway is text-only and
 * cannot fill a PDF form. A disable flag because PDF is the cloud status quo.
 */
export const pdfFormDisabledFlag = defineFeatureFlag({
  key: 'release-growth-disable-pdf-form',
  defaultValue: false,
  description: 'Withhold PDF form template upload',
  builds: 'all',
});

/** PDF form templates withheld. */
export const useIsPdfFormDisabled = (): boolean =>
  useBooleanFlag(pdfFormDisabledFlag.key, pdfFormDisabledFlag.defaultValue);

/**
 * Localized empty-state illustrations on the template tabs. OFF shows the original
 * English artwork, so a bad translation rolls back in Flagsmith without a deploy.
 * `builds: 'all'` because the template library ships on the appliance too.
 */
export const localizedEmptyStateIllustrationsFlag = defineFeatureFlag({
  key: 'release-templates-localized-empty-state-illustrations',
  defaultValue: false,
  description: 'Localized empty-state illustrations on the template library tabs',
  builds: 'all',
});

/** Localized empty-state illustrations on the template library tabs. */
export const useIsLocalizedEmptyStateIllustrationsEnabled = (): boolean =>
  useBooleanFlag(
    localizedEmptyStateIllustrationsFlag.key,
    localizedEmptyStateIllustrationsFlag.defaultValue
  );

/**
 * ENTFR-297 soak gate: author name on each row of the template version-history
 * menu. Default OFF so a production FE that lands before the soak completes still
 * ships date-only rows; Flagsmith flips the author line on without a redeploy.
 * `builds: 'all'` — the template editor (and this menu) ships on the appliance.
 */
export const templateVersionAuthorDisplayFlag = defineFeatureFlag({
  key: 'release-templates-version-author-display',
  defaultValue: false,
  description: 'Show author names on template version-history menu rows',
  builds: 'all',
});

/** Author names on template version-history menu rows. */
export const useIsTemplateVersionAuthorDisplayEnabled = (): boolean =>
  useBooleanFlag(
    templateVersionAuthorDisplayFlag.key,
    templateVersionAuthorDisplayFlag.defaultValue
  );

export const improvedCopyActionsFlag = defineFeatureFlag({
  key: 'release-scribe-improve-copy-actions',
  defaultValue: false,
  description: 'Improved copy actions',
  builds: 'all',
});

export const useIsImprovedCopyActionsEnabled = (): boolean =>
  useBooleanFlag(improvedCopyActionsFlag.key, improvedCopyActionsFlag.defaultValue);

export const isImprovedCopyActionsEnabled = (): boolean =>
  readFlag(improvedCopyActionsFlag.key, improvedCopyActionsFlag.defaultValue);

export const communityDefaultFiltersFlag = defineFeatureFlag({
  key: 'release-scribe-enable-community-default-filters',
  defaultValue: false,
  description: 'Profile-derived template community filters',
  builds: 'all',
});

export const useIsCommunityDefaultFiltersEnabled = (): boolean =>
  useBooleanFlag(
    communityDefaultFiltersFlag.key,
    communityDefaultFiltersFlag.defaultValue
  );

export const nonFillablePdfTemplateFlag = defineFeatureFlag({
  key: 'release-scribe-enable-non-fillable-pdf-template',
  defaultValue: false,
  description: 'Non-fillable PDF templates',
  builds: 'all',
});

export const useIsNonFillablePdfTemplateEnabled = (): boolean =>
  useBooleanFlag(nonFillablePdfTemplateFlag.key, nonFillablePdfTemplateFlag.defaultValue);

export const pinnedTemplateNotegenUiFlag = defineFeatureFlag({
  key: 'release-scribe-enable-pinned-template-notegen-ui',
  defaultValue: false,
  description: 'Pinned template note generation UI',
  builds: 'all',
});

export const useIsPinnedTemplateNotegenUiEnabled = (): boolean =>
  useBooleanFlag(
    pinnedTemplateNotegenUiFlag.key,
    pinnedTemplateNotegenUiFlag.defaultValue
  );

export const globalUsageReadyFlag = defineFeatureFlag({
  key: 'release-scribe-global-usage-ready',
  defaultValue: false,
  description: 'Template global usage readiness',
  builds: 'all',
});

export const useIsGlobalUsageReady = (): boolean =>
  useBooleanFlag(globalUsageReadyFlag.key, globalUsageReadyFlag.defaultValue);

export const sectionsInNotesFlag = defineFeatureFlag({
  key: 'release-scribe-enable-sections-in-notes',
  defaultValue: false,
  description: 'Sections in notes',
  builds: 'all',
});

export const useIsSectionsInNotesEnabled = (): boolean =>
  useBooleanFlag(sectionsInNotesFlag.key, sectionsInNotesFlag.defaultValue);

export const isSectionsInNotesEnabled = (): boolean =>
  readFlag(sectionsInNotesFlag.key, sectionsInNotesFlag.defaultValue);

export const lowLatencyNoteEditingFlag = defineFeatureFlag({
  key: 'release-scribe-low-latency-note-editing',
  defaultValue: false,
  description: 'Low-latency note editing',
  builds: 'all',
});

export const useIsLowLatencyNoteEditingEnabled = (): boolean =>
  useBooleanFlag(lowLatencyNoteEditingFlag.key, lowLatencyNoteEditingFlag.defaultValue);

export const templateDeleteProActionsFlag = defineFeatureFlag({
  key: 'release-scribe-template-delete-pro-actions-ready-after-audit',
  defaultValue: false,
  description: 'Template deletion and Pro Actions recoveries',
  builds: 'all',
});

export const useIsTemplateDeleteProActionsEnabled = (): boolean =>
  useBooleanFlag(
    templateDeleteProActionsFlag.key,
    templateDeleteProActionsFlag.defaultValue
  );

export const isTemplateDeleteProActionsEnabled = (): boolean =>
  readFlag(templateDeleteProActionsFlag.key, templateDeleteProActionsFlag.defaultValue);

export const templateQualityGateKilledFlag = defineFeatureFlag({
  key: 'op-scribe-disable-template-quality-gate',
  defaultValue: false,
  description: 'Disable the template quality gate',
  builds: 'all',
});

export const useIsTemplateQualityGateKilled = (): boolean =>
  useBooleanFlag(
    templateQualityGateKilledFlag.key,
    templateQualityGateKilledFlag.defaultValue
  );
